import { cn } from "@/lib/utils";

interface DeltaIndicatorProps {
  /** Signed numeric delta (e.g. ``score_now - score_prev``).
   *  ``null`` renders a neutral placeholder. */
  delta: number | string | null | undefined;
  /** When true the visual convention is inverted: a negative
   *  delta on a "risk score" feels good (risk decreased), while
   *  a negative delta on a "readiness" feels bad. Default
   *  ``false`` — higher number = better, positive delta = green
   *  arrow up. */
  inverse?: boolean;
  className?: string;
}

/** Render a coloured delta line like ``▲ 2.4`` or ``▼ 1.1``.
 *  Unicode arrows only — no images, no chart library, no SVG
 *  hand-roll. Bahasa Indonesia formatting (decimal comma).
 */
export function DeltaIndicator({
  delta,
  inverse = false,
  className,
}: DeltaIndicatorProps) {
  if (delta === null || delta === undefined || delta === "") {
    return <span className={cn("text-neutral-400", className)}>—</span>;
  }
  const numeric = typeof delta === "string" ? Number(delta) : delta;
  if (!Number.isFinite(numeric)) {
    return <span className={cn("text-neutral-400", className)}>—</span>;
  }

  if (numeric === 0) {
    return (
      <span className={cn("text-neutral-500", className)}>
        ◆ {formatId(0)}
      </span>
    );
  }

  const positive = numeric > 0;
  const goodDirection = inverse ? !positive : positive;
  const arrow = positive ? "▲" : "▼";
  const colour = goodDirection ? "text-success-600" : "text-danger-600";

  return (
    <span className={cn("inline-flex items-center gap-1", colour, className)}>
      {arrow} {formatId(Math.abs(numeric))}
    </span>
  );
}

function formatId(value: number): string {
  return value.toFixed(2).replace(".", ",");
}
